import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ref, get, set, onValue } from 'firebase/database';
import { db } from '../firebase';

// SchedulePage と共通のレスポンシブ CSS
const AuthPageStyles = () => (
  <style>{`
    .np-root {
      min-height: 100vh;
      background: linear-gradient(135deg, #f5f3ff 0%, #e0f2fe 100%);
      display: flex;
      justify-content: center;
      padding: 24px 16px 56px;
      box-sizing: border-box;
      font-family: 'Inter','Helvetica Neue','Hiragino Sans',sans-serif;
    }
    .np-inner  { width: 100%; max-width: 840px; }
    .np-wrap   { max-width: 400px; margin: 0 auto; }
    .np-card   {
      background: #fff; border-radius: 18px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.10); padding: 24px;
    }
    @media (max-width: 640px) {
      .np-root { padding: 12px 10px 48px; }
      .np-card { padding: 18px 14px; border-radius: 14px; }
    }
  `}</style>
);

const AuthHeader = ({ eventName }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9',
  }}>
    <img src="/logo.png" alt="LinkBoard" style={{ height: 34, width: 'auto', flexShrink: 0 }} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5 }}>指名設定</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {eventName || 'イベント'}
      </div>
    </div>
  </div>
);

// ── Firebase reads (public, no auth) ─────────────────────────────────────────

const fetchEventInfo = async (token) => {
  const tokenSnap = await get(ref(db, `nominationTokens/${token}`));
  if (!tokenSnap.exists()) return null;
  const { event_id } = tokenSnap.val();
  const [nameSnap, statusSnap, modeSnap] = await Promise.all([
    get(ref(db, `events/${event_id}/event_name`)),
    get(ref(db, `events/${event_id}/status`)),
    get(ref(db, `events/${event_id}/matching_mode`)),
  ]);
  if (statusSnap.val() !== 'matched') return { expired: true };
  return { eventId: event_id, eventName: nameSnap.val(), matchingMode: modeSnap.val() };
};

const fetchCandidates = async (eventId, role) => {
  if (role === 'cast') {
    // cast は guests + form answers を取得
    const [guestsSnap, appsSnap, formSnap] = await Promise.all([
      get(ref(db, `events/${eventId}/guests`)),
      get(ref(db, `events/${eventId}/applications`)),
      get(ref(db, `events/${eventId}/form_definition`)),
    ]);
    const guests = guestsSnap.val() || {};
    const apps   = appsSnap.val()   || {};
    const formDef = formSnap.val()  || { fields: [] };
    // applications を guest_id でインデックス
    const answersByGuestId = {};
    Object.values(apps).forEach(app => {
      if (app.guest_id) answersByGuestId[app.guest_id] = app.answers || {};
    });
    return { candidates: guests, meta: { answersByGuestId, formFields: formDef.fields || [] } };
  } else {
    // guest は casts を取得
    const castsSnap = await get(ref(db, `events/${eventId}/casts`));
    return { candidates: castsSnap.val() || {}, meta: {} };
  }
};

const findPerson = async (eventId, matchingMode, vrchatName) => {
  const name = vrchatName.trim().toLowerCase();

  if (matchingMode === 'cast_select') {
    const snap = await get(ref(db, `events/${eventId}/casts`));
    const casts = snap.val() || {};
    const found = Object.entries(casts).find(([, c]) => (c.vrchat_name || c.name || '').trim().toLowerCase() === name);
    return found ? { type: 'cast', id: found[0], data: found[1] } : null;
  }
  if (matchingMode === 'guest_select') {
    const snap = await get(ref(db, `events/${eventId}/guests`));
    const guests = snap.val() || {};
    const found = Object.entries(guests).find(([, g]) => (g.vrchat_name || '').trim().toLowerCase() === name);
    return found ? { type: 'guest', id: found[0], data: found[1] } : null;
  }
  // both_select: search both
  const [castsSnap, guestsSnap] = await Promise.all([
    get(ref(db, `events/${eventId}/casts`)),
    get(ref(db, `events/${eventId}/guests`)),
  ]);
  const casts  = castsSnap.val()  || {};
  const guests = guestsSnap.val() || {};
  const cast   = Object.entries(casts).find(([, c])  => (c.vrchat_name || c.name || '').trim().toLowerCase() === name);
  if (cast) return { type: 'cast', id: cast[0], data: cast[1] };
  const guest  = Object.entries(guests).find(([, g]) => (g.vrchat_name || '').trim().toLowerCase() === name);
  if (guest) return { type: 'guest', id: guest[0], data: guest[1] };
  return null;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Avatar = ({ imageUrl, name, size = 40, color = '#6c63ff' }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: imageUrl ? 'transparent' : color,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: size * 0.4, overflow: 'hidden',
  }}>
    {imageUrl
      ? <img src={imageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      : (name || '?').charAt(0).toUpperCase()
    }
  </div>
);

const LinkRow = ({ label, url }) => url ? (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', minWidth: 24 }}>{label}</span>
    <a href={url} target="_blank" rel="noreferrer"
      style={{ fontSize: 12, color: '#6c63ff', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}
      onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
    >{url}</a>
  </div>
) : null;

// 指名リスト（上部）
const NominationList = ({ picks, candidates, onRemove, onMove }) => {
  const [dragIdx,     setDragIdx]     = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  if (picks.length === 0) return (
    <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>
      まだ指名していません。下の一覧から選んでください。
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {picks.map((id, i) => {
        const c = candidates[id];
        if (!c) return null;
        const name = c.vrchat_name || c.name || '?';
        const isDragging  = dragIdx === i;
        const isOver      = dragOverIdx === i;
        return (
          <div
            key={id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={e => { e.preventDefault(); setDragOverIdx(i); }}
            onDrop={() => {
              if (dragIdx === null || dragIdx === i) return;
              onMove(dragIdx, i);
              setDragIdx(null); setDragOverIdx(null);
            }}
            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: isOver ? '#f5f3ff' : isDragging ? '#fafafa' : '#fff',
              border: `1px solid ${isOver ? '#c4b5fd' : '#e2e8f0'}`,
              borderRadius: 10, padding: '10px 12px',
              opacity: isDragging ? 0.5 : 1,
              cursor: 'grab', transition: 'all 0.1s',
            }}
          >
            <span style={{ fontSize: 11, color: '#94a3b8', userSelect: 'none' }}>⠿</span>
            <span style={{
              minWidth: 32, height: 22, borderRadius: 6,
              background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800, flexShrink: 0, padding: '0 6px',
            }}>{i + 1}位</span>
            <Avatar imageUrl={c.image_url} name={name} size={32} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{name}</span>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button onClick={() => onMove(i, i - 1)} disabled={i === 0}
                style={{ ...btnSm, opacity: i === 0 ? 0.3 : 1 }}>▲</button>
              <button onClick={() => onMove(i, i + 1)} disabled={i === picks.length - 1}
                style={{ ...btnSm, opacity: i === picks.length - 1 ? 0.3 : 1 }}>▼</button>
              <button onClick={() => onRemove(id)} style={{ ...btnSm, color: '#ef4444', borderColor: '#fca5a5' }}>解除</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// キャスト候補カード（ゲストがキャストを選ぶ）
const CastCandidateCard = ({ id, cast, rank, onAdd, onRemove }) => {
  const name = cast.vrchat_name || cast.name || '?';
  const nominated = rank !== null;
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8edf5', padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <Avatar imageUrl={cast.image_url} name={name} size={48} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{name}</div>
        {(cast.tags || []).length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
            {cast.tags.map(tag => (
              <span key={tag} style={{ background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>#{tag}</span>
            ))}
          </div>
        )}
        <LinkRow label="VRC" url={cast.vrchat_profile} />
        <LinkRow label="𝕏"  url={cast.x_url} />
      </div>
      <div style={{ flexShrink: 0 }}>
        {nominated ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800,
            }}>{rank + 1}位</span>
            <button onClick={() => onRemove(id)} style={{ ...btnSm, color: '#ef4444', borderColor: '#fca5a5', fontSize: 10 }}>解除</button>
          </div>
        ) : (
          <button onClick={() => onAdd(id)} style={{
            padding: '7px 12px', borderRadius: 8, border: '1.5px solid #6c63ff',
            background: '#fff', color: '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}>+ 指名する</button>
        )}
      </div>
    </div>
  );
};

// ゲスト候補カード（キャストがゲストを選ぶ）
const GuestCandidateCard = ({ id, guest, rank, formFields, answers, onAdd, onRemove }) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const name = guest.vrchat_name || '?';
  const nominated = rank !== null;
  const hasAnswers = formFields.length > 0 && Object.keys(answers || {}).length > 0;
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8edf5', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Avatar imageUrl={guest.image_url} name={name} size={48} color="#10b981" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{name}</div>
          <LinkRow label="VRC" url={guest.vrchat_profile} />
          <LinkRow label="𝕏"  url={guest.x_url} />
          {hasAnswers && (
            <button onClick={() => setShowAnswers(v => !v)} style={{
              marginTop: 6, padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>
              {showAnswers ? '▲ 応募内容を閉じる' : '▼ 応募内容を見る'}
            </button>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          {nominated ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800,
              }}>{rank + 1}位</span>
              <button onClick={() => onRemove(id)} style={{ ...btnSm, color: '#ef4444', borderColor: '#fca5a5', fontSize: 10 }}>解除</button>
            </div>
          ) : (
            <button onClick={() => onAdd(id)} style={{
              padding: '7px 12px', borderRadius: 8, border: '1.5px solid #6c63ff',
              background: '#fff', color: '#6c63ff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}>+ 指名する</button>
          )}
        </div>
      </div>
      {showAnswers && hasAnswers && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {formFields.map(field => {
            const ans = answers[field.id];
            if (!ans) return null;
            return (
              <div key={field.id}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>{field.label}</div>
                <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ans}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── CastNamePicker（SchedulePageと同じコンボボックス）────────────────────────
const CastNamePicker = ({ casts, value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const allNames = Object.values(casts)
    .map(c => (c.vrchat_name || c.name || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'ja'));

  const q = value.trim().toLowerCase();
  const filtered = q ? allNames.filter(n => n.toLowerCase().includes(q)) : allNames;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const inputStyle = {
    padding: '13px 16px', borderRadius: 12,
    border: `1.5px solid ${error ? '#ef4444' : open ? '#6c63ff' : '#e2e8f0'}`,
    fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box',
    transition: 'border-color 0.15s', paddingRight: 42,
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={inputStyle}
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="VRChat名を入力または選択"
          autoFocus
        />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            position: 'absolute', top: 0, right: 0, height: '100%', width: 40,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8',
          }}
        >
          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none', fontSize: 12 }}>▼</span>
        </button>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', padding: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#94a3b8' }}>一致する名前がありません</div>
          ) : (
            filtered.map(name => {
              const active = name.toLowerCase() === q;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name); setOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: active ? '#f5f3ff' : 'transparent',
                    color: active ? '#6c63ff' : '#334155',
                    fontSize: 14, fontWeight: active ? 800 : 600,
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {name}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── スタイル定数 ──────────────────────────────────────────────────────────────
const btnSm = {
  padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
  background: '#fff', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer',
};

// ── Main page ─────────────────────────────────────────────────────────────────

const NominationPage = () => {
  const { token } = useParams();

  const [pageState, setPageState] = useState('loading'); // loading|invalid|input|identifying|picking|saved
  const [invalidReason, setInvalidReason] = useState('');
  const [eventInfo,   setEventInfo]   = useState(null); // { eventId, eventName, matchingMode }
  const [nameInput,   setNameInput]   = useState('');
  const [nameError,   setNameError]   = useState('');
  const [identified,  setIdentified]  = useState(null); // { type, id, data }
  const [candidates,  setCandidates]  = useState({});   // id → cast/guest object
  const [candidateMeta, setCandidateMeta] = useState({}); // { answersByGuestId, formFields }
  const [picks,            setPicks]            = useState([]);   // ordered array of IDs
  const [saveStatus,       setSaveStatus]       = useState('idle');
  const [castListForPicker, setCastListForPicker] = useState({});  // cast_select 用の事前取得

  // 初期ロード
  useEffect(() => {
    fetchEventInfo(token).then(async info => {
      if (!info)        { setInvalidReason('このURLは無効または期限切れです'); setPageState('invalid'); return; }
      if (info.expired) { setInvalidReason('現在、指名設定を受け付けていません'); setPageState('invalid'); return; }
      setEventInfo(info);
      // cast_select モードはキャスト一覧を先読みしてピッカーに渡す
      if (info.matchingMode === 'cast_select') {
        const snap = await get(ref(db, `events/${info.eventId}/casts`));
        setCastListForPicker(snap.val() || {});
      }
      setPageState('input');
    }).catch(() => {
      setInvalidReason('読み込みに失敗しました');
      setPageState('invalid');
    });
  }, [token]);

  // トークンのリアルタイム監視 — 締め切られたら即時反映
  useEffect(() => {
    if (!eventInfo) return;
    const unsubscribe = onValue(ref(db, `nominationTokens/${token}`), (snap) => {
      if (!snap.exists()) {
        setInvalidReason('指名の受付は締め切られました');
        setPageState('invalid');
      }
    });
    return () => unsubscribe();
  }, [eventInfo, token]);

  const handleIdentify = async () => {
    if (!nameInput.trim()) { setNameError('VRChat名を入力してください'); return; }
    setPageState('identifying');
    setNameError('');
    try {
      const person = await findPerson(eventInfo.eventId, eventInfo.matchingMode, nameInput);
      if (!person) { setNameError('このVRChat名は見つかりませんでした'); setPageState('input'); return; }
      setIdentified(person);

      // 既存の指名を読み込む
      const path = person.type === 'cast'
        ? `events/${eventInfo.eventId}/casts/${person.id}/draft_picks`
        : `events/${eventInfo.eventId}/guests/${person.id}/draft_picks`;
      const existingSnap = await get(ref(db, path));
      const existing = existingSnap.val() || [];
      setPicks(Array.isArray(existing) ? existing : []);

      // 候補一覧を取得
      const role = person.type; // cast or guest
      const { candidates: cands, meta } = await fetchCandidates(eventInfo.eventId, role);
      setCandidates(cands);
      setCandidateMeta(meta);
      setPageState('picking');
    } catch {
      setNameError('読み込みに失敗しました。もう一度お試しください');
      setPageState('input');
    }
  };

  const handleAdd = (id) => {
    if (!picks.includes(id)) setPicks(p => [...p, id]);
  };

  const handleRemove = (id) => {
    setPicks(p => p.filter(x => x !== id));
  };

  const handleMove = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= picks.length) return;
    const next = [...picks];
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    setPicks(next);
  };

  const handleSave = async () => {
    if (!identified) return;
    setSaveStatus('saving');
    try {
      const path = identified.type === 'cast'
        ? `events/${eventInfo.eventId}/casts/${identified.id}/draft_picks`
        : `events/${eventInfo.eventId}/guests/${identified.id}/draft_picks`;
      await set(ref(db, path), picks.length > 0 ? picks : null);
      setSaveStatus('saved');
      setPageState('saved');
    } catch {
      setSaveStatus('error');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (pageState === 'loading') return (
    <PageWrap eventName="">
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>読み込み中...</div>
    </PageWrap>
  );

  if (pageState === 'invalid') return (
    <PageWrap eventName="">
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <p style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{invalidReason}</p>
        <p style={{ fontSize: 13, color: '#94a3b8' }}>URLをご確認ください</p>
      </div>
    </PageWrap>
  );

  if (pageState === 'saved') return (
    <PageWrap eventName={eventInfo?.eventName}>
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
        <p style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>指名設定を保存しました</p>
        <p style={{ fontSize: 13, color: '#94a3b8' }}>{identified?.data?.vrchat_name || identified?.data?.name} さんの指名設定が完了しました</p>
        <button onClick={() => { setPageState('picking'); setSaveStatus('idle'); }}
          style={{ marginTop: 20, padding: '9px 20px', borderRadius: 8, border: '1.5px solid #6c63ff', background: '#fff', color: '#6c63ff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          内容を修正する
        </button>
      </div>
    </PageWrap>
  );

  if (pageState === 'input' || pageState === 'identifying') {
    const isCastSelect = eventInfo?.matchingMode === 'cast_select';
    return (
      <>
        <AuthPageStyles />
        <div className="np-root">
          <div className="np-inner">
            <div className="np-wrap">
              <div className="np-card">
                <AuthHeader eventName={eventInfo?.eventName} />
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.75, margin: '0 0 20px' }}>
                  {isCastSelect
                    ? <>登録されているVRChat名を入力、またはリストから選択してください。<br />希望するゲストを選んで指名設定を保存できます。</>
                    : <>このイベントに登録されているVRChat名を入力してください。<br />希望する相手を選んで指名設定を保存できます。</>
                  }
                </p>
                <form
                  onSubmit={e => { e.preventDefault(); handleIdentify(); }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  {isCastSelect ? (
                    <CastNamePicker
                      casts={castListForPicker}
                      value={nameInput}
                      onChange={v => { setNameInput(v); setNameError(''); }}
                      error={!!nameError}
                    />
                  ) : (
                    <input
                      value={nameInput}
                      onChange={e => { setNameInput(e.target.value); setNameError(''); }}
                      placeholder="VRChat表示名"
                      autoFocus
                      style={{
                        padding: '13px 16px', borderRadius: 12,
                        border: `1.5px solid ${nameError ? '#ef4444' : '#e2e8f0'}`,
                        fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box',
                        transition: 'border-color 0.15s',
                      }}
                    />
                  )}
                  {nameError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', lineHeight: 1.6 }}>
                      {nameError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={!nameInput.trim() || pageState === 'identifying'}
                    style={{
                      background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none',
                      borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, width: '100%',
                      boxShadow: '0 4px 14px rgba(108,99,255,0.38)', transition: 'opacity 0.15s',
                      opacity: (!nameInput.trim() || pageState === 'identifying') ? 0.5 : 1,
                      cursor: (!nameInput.trim() || pageState === 'identifying') ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {pageState === 'identifying' ? '確認中...' : '確認する →'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // picking
  const roleLabel = identified?.type === 'cast' ? 'キャスト' : 'ゲスト';
  const candidateLabel = identified?.type === 'cast' ? 'ゲスト' : 'キャスト';
  const displayName = identified?.data?.vrchat_name || identified?.data?.name || '?';

  const handleBackToAuth = () => {
    setIdentified(null);
    setCandidates({});
    setPicks([]);
    setNameInput('');
    setNameError('');
    setSaveStatus('idle');
    setPageState('input');
  };

  const welcomeBar = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#f5f3ff', border: '1px solid #ddd6fe',
      borderRadius: 10, padding: '10px 14px', marginTop: 16,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: '#6c63ff', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 800, flexShrink: 0,
      }}>
        {displayName.charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
        <div style={{ fontSize: 11, color: '#6c63ff', fontWeight: 600 }}>{roleLabel}</div>
      </div>
      <button
        onClick={handleBackToAuth}
        style={{
          flexShrink: 0, background: 'transparent', border: '1px solid #ddd6fe',
          borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600,
          color: '#6c63ff', cursor: 'pointer',
        }}
      >
        変更
      </button>
    </div>
  );

  return (
    <PageWrap eventName={eventInfo?.eventName} subheader={welcomeBar}>

      {/* 指名リスト */}
      <Section title={picks.length === 0 ? '指名希望リスト（希望順に並べてください）' : `指名希望リスト（${picks.length}件 · 上から1位）`}>
        <NominationList
          picks={picks}
          candidates={candidates}
          onRemove={handleRemove}
          onMove={handleMove}
        />
        <div style={{ marginTop: 16 }}>
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            style={{
              width: '100%', padding: '12px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
              color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
              opacity: saveStatus === 'saving' ? 0.7 : 1,
            }}
          >
            {saveStatus === 'saving' ? '保存中...' : '指名を保存する'}
          </button>
          {saveStatus === 'error' && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>保存に失敗しました。もう一度お試しください</p>
          )}
        </div>
      </Section>

      {/* 候補一覧 */}
      <Section title={`${candidateLabel}一覧`} style={{ marginTop: 24 }}>
        {Object.keys(candidates).length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>{candidateLabel}がまだいません</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(candidates).map(([id, person]) => {
              const rank = picks.indexOf(id);
              return identified?.type === 'cast' ? (
                <GuestCandidateCard
                  key={id} id={id} guest={person}
                  rank={rank === -1 ? null : rank}
                  formFields={candidateMeta.formFields || []}
                  answers={(candidateMeta.answersByGuestId || {})[id] || {}}
                  onAdd={handleAdd} onRemove={handleRemove}
                />
              ) : (
                <CastCandidateCard
                  key={id} id={id} cast={person}
                  rank={rank === -1 ? null : rank}
                  onAdd={handleAdd} onRemove={handleRemove}
                />
              );
            })}
          </div>
        )}
      </Section>
    </PageWrap>
  );
};

// ── Layout helpers ────────────────────────────────────────────────────────────

const PageWrap = ({ eventName, subheader, children }) => (
  <>
    <AuthPageStyles />
    <div className="np-root" style={{ alignItems: 'flex-start' }}>
      <div className="np-inner">
        <div className="np-card" style={{ marginBottom: 16 }}>
          <AuthHeader eventName={eventName} />
          {subheader}
        </div>
        {children}
      </div>
    </div>
  </>
);

const Section = ({ title, children, style }) => (
  <div style={{ background: '#fff', borderRadius: 16, padding: '20px', border: '1px solid #e8edf5', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', ...style }}>
    <h2 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{title}</h2>
    {children}
  </div>
);

export default NominationPage;
